import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { AccountUser } from '../models/account-user.model';
import { LexbeCase } from '../models/case.model';
import { AccountUsage } from '../models/account-usage.model';
import { ErrorHandlerService } from './error-handler.service';
import { LexbeCodingField } from '../models/lexbe-coding-field.model';
import { FileUtility } from '../utilities/file.utility';

@Injectable({
	providedIn: 'root'
})
export class AccountAdminService {

	constructor(private http: HttpClient, private errorHandler: ErrorHandlerService, private appConfigService: AppConfigService, private fileUtility: FileUtility){
	}

	getAccountUsers(accountId: string): Observable<AccountUser[]> {
		return this.http.get<AccountUser[]>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get account users`)));
	}

	getAccountUserRoleIds(accountId: string, userId: string): Observable<string[]> {
		return this.http.get<string[]>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users/${userId}/Roles`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get account user roles`)));
	}

	getActiveAccountUsers(accountId: string): Observable<AccountUser[]> {
		return this.http.get<AccountUser[]>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users/Active`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get active account users`)));
	}

	getCases(accountId: string): Observable<LexbeCase[]> {
		return this.http.get<LexbeCase[]>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Cases`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get cases in account`)));
	}

	getSelectedCustomFields(accountId: string, userId: string): Observable<LexbeCodingField[]> {
		return this.http.get<LexbeCodingField[]>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users/${userId}/CustomFields`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get selected custom fields for account user`)));
	}

	getCustomFields(accountId: string): Observable<LexbeCodingField[]> {
		return this.http.get<LexbeCodingField[]>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/CustomFields`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get available custom fields for account`)));
	}

	createUserInAccount(accountId: string, user: AccountUser): Observable<string> {
		return this.http.post<string>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users`, user);
	}

	updateAccountUser(accountId: string, user: AccountUser) {
		return this.http.put(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users`, user);
	}

	removewUserFromAccount(accountId: string, userId: string) {
		return this.http.delete(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users/${userId}`).pipe(catchError(this.errorHandler.handleError<any>(`delete account user from account`)));
	}

	updateAccountUserAccess(accountId: string, userIds: string[]): Observable<string> {
		return this.http.patch<string>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Accounts/${accountId}/Users`, userIds);
	}

	updateUserAccountAccess(userId: string, accountIds: string[]): Observable<string> {
		return this.http.patch<string>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Users/${userId}/Accounts`, accountIds);
	}

	updateUserAccountRoles(userId: string, accountId: string, roleIds: string[]): Observable<string> {
		return this.http.patch<string>(`${this.appConfigService.getConfig().webApi}/api2/UserAccountAdmin/Users/${userId}/Accounts/${accountId}`, roleIds);
	}

	getMonthlyAccountUsage(accountId: string, showCurrent: boolean) {
		return this.http.get<AccountUsage[]>(`${this.appConfigService.getConfig().webApi}/api2/Usages/Accounts/${accountId}?showCurrent=${showCurrent}`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get monthly account usage`)));
	}

	getMonthlyCaseUsage(accountId: string, showCurrent: boolean) {
		return this.http.get<AccountUsage[]>(`${this.appConfigService.getConfig().webApi}/api2/Usages/Accounts/${accountId}/Cases?showCurrent=${showCurrent}`)
			.pipe(catchError(this.errorHandler.handleError<any>(`get monthly case usage`)));
	}
}
